import { debounce, sum } from 'lodash';
import { memo, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { SPACING_SCALING_FACTOR as GAP_BETWEEN_CHIPS } from '../../../constants';
import type { ITheme } from '../../../utils/theme';
import { getSelectText } from '../helper-functions/getSelectText';
import Chip from './chip';

const PADDING_AROUND_CHIPS = 16;

interface IChips {
  selectValue: string[];
  optionsMap: Record<string, string>;
  handleRemoveSelectValue: (value: string) => void;
  closeButtonLabel?: string;
  disabled?: boolean;
}

const ChipsInSingleRow = ({
  selectValue,
  optionsMap,
  handleRemoveSelectValue,
  closeButtonLabel,
  disabled = false,
}: IChips) => {
  const chipsWrapperRef = useRef<HTMLDivElement>(null);
  const [chipWidths, setChipWidths] = useState<number[]>();
  const [chipsToShow, setChipsToShow] = useState<string[]>([]);
  const [isHiddenChips, setIsHiddenChips] = useState(false);

  const chipsWrapperWidth = (chipsWrapperRef?.current?.offsetWidth ?? 200) - 40;

  useEffect(() => {
    const calculateChipWidths = () => {
      if (chipsWrapperRef.current) {
        const chipElements = chipsWrapperRef.current
          .children as unknown as HTMLDivElement[];
        // iterating over chipElements doesn't work
        const updatedChips = selectValue?.map((_, index) => {
          const chipWidth = chipElements?.[index]?.offsetWidth;
          return chipWidth;
        });
        setChipWidths(updatedChips);
      }
    };

    calculateChipWidths();
    const debouncedCalculateChipWidths = debounce(calculateChipWidths, 600);
    window.addEventListener('resize', debouncedCalculateChipWidths);
    return () => {
      window.removeEventListener('resize', calculateChipWidths);
    };
  }, [selectValue]);

  useEffect(() => {
    if (!chipWidths || !chipsWrapperWidth || selectValue.length === 0) {
      setChipsToShow([]);
      setIsHiddenChips(false);
      return;
    }
    // if there's 1 chip, always show it as it will truncate
    if (selectValue.length === 1) {
      setChipsToShow(selectValue);
      setIsHiddenChips(false);
      return;
    }
    // if all chips will fit:
    if (sum(chipWidths) < chipsWrapperWidth) {
      setChipsToShow(selectValue);
      setIsHiddenChips(false);
      return;
    }
    const currentChipsToShow: string[] = [selectValue[0]];
    for (const [index, width] of chipWidths.slice(1).entries()) {
      if (width + GAP_BETWEEN_CHIPS > chipsWrapperWidth) {
        setChipsToShow(currentChipsToShow);
        setIsHiddenChips(true);
        break;
      }
      if (
        sum(chipWidths.slice(0, index + 1)) +
          width +
          currentChipsToShow.length * GAP_BETWEEN_CHIPS >
        chipsWrapperWidth
      ) {
        setChipsToShow(currentChipsToShow);
        setIsHiddenChips(true);
        break;
      }
      currentChipsToShow.push(selectValue[index + 1]);
    }
  }, [chipWidths, selectValue, chipsWrapperWidth]);
  const numberOfChipsToShow = chipsToShow.length;
  const widthOfChips =
    sum(chipWidths?.slice(0, numberOfChipsToShow)) +
    numberOfChipsToShow * GAP_BETWEEN_CHIPS +
    PADDING_AROUND_CHIPS;
  const numberHiddenChips = selectValue.length - numberOfChipsToShow;

  return (
    <Wrapper>
      <ChipsWrapper ref={chipsWrapperRef} id="chips-wrapper">
        {selectValue?.map((value) => {
          return (
            <Chip
              key={value}
              onClick={() => handleRemoveSelectValue(value)}
              text={getSelectText(value, optionsMap)}
              hidden={!chipsToShow.includes(value)}
              closeButtonLabel={closeButtonLabel}
              disabled={disabled}
            />
          );
        })}
      </ChipsWrapper>
      {isHiddenChips && (
        <Overflow left={`${widthOfChips}px`}>
          {numberOfChipsToShow > 1 ? '...' : ''}
          <Chip text={`+ ${numberHiddenChips}`} disabled={disabled}></Chip>
        </Overflow>
      )}
    </Wrapper>
  );
};

export default memo(ChipsInSingleRow);

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 100%;
  width: 100%;
`;

const Overflow = styled.div`
  ${({ theme, left }: { theme: ITheme; left: string }) => css`
    position: absolute;
    left: ${left};
    bottom: 8px;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: ${theme.spacing(0.5)};
  `}
`;

export const ChipsWrapper = styled.div`
  white-space: nowrap;
  display: flex;
  gap: ${GAP_BETWEEN_CHIPS}px;
  max-width: 95%;
  width: 100%;
  min-width: 90%;
  padding-right: 50px;
`;
